import { useEffect, useMemo, useState } from "react";
import { useRequest } from "../hooks/useRequest";
import {
  getThirdPartyOrders,
  exportThirdPartyOrdersCsv,
  ThirdPartyOrdersQuery,
  ThirdPartyOrder,
  MAX_EXPORT_RANGE_DAYS,
} from "../api/thirdPartyOrders";
import { formatCents, formatDateTime, formatNumber } from "../utils/format";
import "./ThirdPartyOrders.scss";

/** 每页可选条数（上限 50，见下游 t-f <= 50） */
const PAGE_SIZES = [20, 50];

/** 空值占位 */
const DASH = "—";

/** 一天的毫秒数 */
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** epoch 毫秒 → ISO 字符串；非法/缺失时返回 null */
function epochToIso(ms: number | null | undefined): string | null {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms <= 0) return null;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/** 金额（美分）→ 展示文案，如 12990 → "$129.90"；不拼接货币代码 */
function formatAmount(cents: number | null | undefined): string {
  if (typeof cents !== "number" || !Number.isFinite(cents)) return DASH;
  return formatCents(cents);
}

/** 把 <input type="date"> 的 yyyy-mm-dd 转成 ISO instant（含边界时刻） */
function dateToInstant(date: string, endOfDay = false): string | undefined {
  if (!date) return undefined;
  const d = new Date(`${date}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/** Date → yyyy-mm-dd（本地时区，供 <input type="date"> 使用） */
function toDateInput(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** 默认日期范围：最近 30 天（含今天），控制在导出跨度上限内 */
const DEFAULT_RANGE_DAYS = 30;
function defaultDateRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - (DEFAULT_RANGE_DAYS - 1));
  return { from: toDateInput(from), to: toDateInput(to) };
}

/** 生成分页页码序列（含省略号）：1 … (c-1) c (c+1) … last */
function buildPageList(current: number, totalPages: number): (number | "…")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const pages: (number | "…")[] = [1];
  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages - 1, current + 1);
  if (start > 2) pages.push("…");
  for (let i = start; i <= end; i++) pages.push(i);
  if (end < totalPages - 1) pages.push("…");
  pages.push(totalPages);
  return pages;
}

/** 计算日期跨度（天，含首尾），格式非法返回 null */
function rangeDays(from: string, to: string): number | null {
  const f = new Date(`${from}T00:00:00.000`);
  const t = new Date(`${to}T00:00:00.000`);
  if (Number.isNaN(f.getTime()) || Number.isNaN(t.getTime())) return null;
  return Math.floor((t.getTime() - f.getTime()) / MS_PER_DAY) + 1;
}

const ThirdPartyOrders = () => {
  useEffect(() => {
    document.title = "Third-party Orders | CloudStore API";
  }, []);

  // 默认日期范围：最近 30 天（在挂载时计算一次）
  const [initialRange] = useState(defaultDateRange);

  // 已提交（生效中）的查询条件：默认带上最近 30 天
  const [query, setQuery] = useState<ThirdPartyOrdersQuery>(() => ({
    page: 1,
    size: 20,
    from: dateToInstant(initialRange.from),
    to: dateToInstant(initialRange.to, true),
  }));

  // 表单输入（未点 Search 前不生效）
  const [merchantId, setMerchantId] = useState("");
  const [dateFrom, setDateFrom] = useState(initialRange.from);
  const [dateTo, setDateTo] = useState(initialRange.to);

  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);

  const { data, loading, error } = useRequest(
    () => getThirdPartyOrders(query),
    [query]
  );

  const orders = data?.orders ?? [];
  const total = data?.total ?? 0;
  // 下游查询失败时（200 + error 字段）也作为错误提示展示
  const downstreamError = data?.error ?? null;
  const page = query.page ?? 1;
  const size = query.size ?? 20;
  const totalPages = Math.max(1, Math.ceil(total / size));

  const rangeStart = total === 0 ? 0 : (page - 1) * size + 1;
  const rangeEnd = Math.min(page * size, total);

  const pageList = useMemo(
    () => buildPageList(page, totalPages),
    [page, totalPages]
  );

  /** 点击 Search：收集表单，回到第 1 页 */
  const handleSearch = () => {
    setQuery({
      page: 1,
      size,
      merchantOrderId: merchantId.trim() || undefined,
      from: dateToInstant(dateFrom),
      to: dateToInstant(dateTo, true),
    });
  };

  const goToPage = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    setQuery((q) => ({ ...q, page: p }));
  };

  const changeSize = (newSize: number) => {
    setQuery((q) => ({ ...q, size: newSize, page: 1 }));
  };

  const handleExport = async () => {
    setExportErr(null);

    // 导出必须指定时间范围（from/to 必填）
    if (!dateFrom || !dateTo) {
      setExportErr("Please select a date range to export.");
      return;
    }
    // 跨度不得超过配置上限（默认 32 天）
    const span = rangeDays(dateFrom, dateTo);
    if (span == null) {
      setExportErr("Invalid date range.");
      return;
    }
    if (span > MAX_EXPORT_RANGE_DAYS) {
      setExportErr(
        `Date range cannot exceed ${MAX_EXPORT_RANGE_DAYS} days for export.`
      );
      return;
    }

    const from = dateToInstant(dateFrom);
    const to = dateToInstant(dateTo, true);
    if (!from || !to) {
      setExportErr("Invalid date range.");
      return;
    }

    setExporting(true);
    try {
      const blob = await exportThirdPartyOrdersCsv({
        from,
        to,
        merchantOrderId: merchantId.trim() || undefined,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "third-party-orders-export.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportErr(e instanceof Error ? e.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  const renderDateTime = (ms: number): string => {
    const iso = epochToIso(ms);
    return iso ? formatDateTime(iso) : DASH;
  };

  return (
    <div className="cs-tp-orders">
      <h1 className="cs-tp-orders__title">Third-party Orders</h1>

      {/* 筛选栏 */}
      <div className="cs-tp-orders__filters">
        <input
          className="cs-tp-orders__input"
          placeholder="Merchant Order ID"
          value={merchantId}
          onChange={(e) => setMerchantId(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
        />

        <div className="cs-tp-orders__field">
          <span className="cs-tp-orders__field-label">Created At</span>
          <input
            type="date"
            className="cs-tp-orders__date"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
          />
          <span className="cs-tp-orders__date-sep">–</span>
          <input
            type="date"
            className="cs-tp-orders__date"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <button
          type="button"
          className="cs-tp-orders__btn cs-tp-orders__btn--primary"
          onClick={handleSearch}
        >
          Search
        </button>
        <button
          type="button"
          className="cs-tp-orders__btn cs-tp-orders__btn--primary"
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? "Exporting…" : "Export CSV"}
        </button>
      </div>

      {exportErr && <div className="cs-tp-orders__error">{exportErr}</div>}

      {/* 表格 */}
      <div className="cs-tp-orders__table-wrap">
        <table className="cs-tp-orders__table">
          <thead>
            <tr>
              <th className="cs-tp-orders__th">Merchant Order ID</th>
              <th className="cs-tp-orders__th">Created At</th>
              <th className="cs-tp-orders__th">Currency</th>
              <th className="cs-tp-orders__th">Order Price</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td className="cs-tp-orders__state" colSpan={4}>
                  Loading…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td
                  className="cs-tp-orders__state cs-tp-orders__state--error"
                  colSpan={4}
                >
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && downstreamError && orders.length === 0 && (
              <tr>
                <td
                  className="cs-tp-orders__state cs-tp-orders__state--error"
                  colSpan={4}
                >
                  {downstreamError}
                </td>
              </tr>
            )}
            {!loading && !error && !downstreamError && orders.length === 0 && (
              <tr>
                <td className="cs-tp-orders__state" colSpan={4}>
                  No orders found.
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              orders.map((o: ThirdPartyOrder, i) => (
                <tr key={o.id || i}>
                  <td className="cs-tp-orders__td cs-tp-orders__id">
                    {o.merchantOrderId || DASH}
                  </td>
                  <td className="cs-tp-orders__td">
                    {renderDateTime(o.placedAt)}
                  </td>
                  <td className="cs-tp-orders__td">{o.currency || DASH}</td>
                  <td className="cs-tp-orders__td cs-tp-orders__amount">
                    {formatAmount(o.amountCents)}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* 底部：每页条数 + 计数 + 分页 */}
      <div className="cs-tp-orders__footer">
        <div className="cs-tp-orders__footer-left">
          <span className="cs-tp-orders__per-page-label">per page</span>
          <select
            className="cs-tp-orders__per-page"
            value={size}
            onChange={(e) => changeSize(Number(e.target.value))}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <span className="cs-tp-orders__count">
            Showing {formatNumber(rangeStart)}-{formatNumber(rangeEnd)} of{" "}
            {formatNumber(total)} orders
          </span>
        </div>

        <div className="cs-tp-orders__pager">
          <button
            type="button"
            className="cs-tp-orders__page-btn"
            disabled={page <= 1}
            onClick={() => goToPage(page - 1)}
            aria-label="Previous page"
          >
            ‹
          </button>
          {pageList.map((p, idx) =>
            p === "…" ? (
              <span key={`gap-${idx}`} className="cs-tp-orders__page-gap">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                className={`cs-tp-orders__page-btn${
                  p === page ? " cs-tp-orders__page-btn--active" : ""
                }`}
                onClick={() => goToPage(p)}
              >
                {p}
              </button>
            )
          )}
          <button
            type="button"
            className="cs-tp-orders__page-btn"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
            aria-label="Next page"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThirdPartyOrders;
